"use client";

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import styles from "../css/paymentTypes.module.css";
import { LuBanknote, LuFileText, LuBuilding2, LuArrowLeftRight } from "react-icons/lu";
import { FiCreditCard, FiSmartphone } from "react-icons/fi";
import { Split } from "lucide-react";
import { getPaymentTotal as apiGetPaymentTotal, setPaymentTotal as apiSetPaymentTotal, getSelectedCustomer as apiGetSelectedCustomer } from "../../../../api/frontShop/CheckOutAPI";

export default function PaymentTypesView() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState(null);
  const [hasCustomer, setHasCustomer] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const fromDebtorsLocal =
          !!(location.state && location.state.fromDebtors) ||
          (typeof window !== "undefined" && window.location.pathname.startsWith("/frontshop/debtors/payment-types"));

        if (location.state && typeof location.state.total === "number") {
          setAmount(location.state.total);
        } else if (fromDebtorsLocal) {
          try {
            const st = sessionStorage.getItem("debtors_total");
            const n = Number(st);
            if (Number.isFinite(n)) setAmount(n);
          } catch {}
        }

        if (!fromDebtorsLocal) {
          const { data: tot } = await apiGetPaymentTotal();
          if (!cancelled && tot?.success && typeof tot.total === "number") {
            setAmount(tot.total);
          }
          await apiSetPaymentTotal();
        }
        const { data: sel } = await apiGetSelectedCustomer();
        if (!cancelled) setHasCustomer(Boolean(sel && sel.patientid));
      } catch {}
    }
    load();
    return () => { cancelled = true; };
  }, [location.state]);

  const displayAmount = amount != null ? amount.toFixed(2) : "0.00";
  const fromDebtors =
    !!(location.state && location.state.fromDebtors) ||
    location.pathname.startsWith("/frontshop/debtors/payment-types");
  const basePath = fromDebtors
    ? "/frontshop/debtors/payment-types"
    : "/frontshop/payment-types";

  const handleBack = () => {
    if (fromDebtors) {
      navigate("/frontshop/debtors");
      return;
    }
    navigate("/frontshop");
  };

  const baseMethods = [
    { id: "cash", label: "Cash", icon: LuBanknote, className: styles.cash },
    { id: "credit", label: "Credit", icon: LuFileText, className: styles.credit },
    { id: "card", label: "Card", icon: FiCreditCard, className: styles.card },
    { id: "nedbank", label: "Nedbank", icon: LuBuilding2, className: styles.nedbank },
    { id: "ikhokha", label: "Ikhokha", icon: FiSmartphone, className: styles.ikhokha },
    { id: "eft", label: "EFT", icon: LuArrowLeftRight, className: styles.eft },
    { id: "split", label: "Split", icon: Split, className: styles.split },
  ];

  const methods = fromDebtors
    ? baseMethods.filter((m) =>
        ["cash", "card", "nedbank", "ikhokha", "eft"].includes(m.id)
      )
    : baseMethods;

  return (
    <div className={styles.wrapper}>
      <Navbar onBack={handleBack} />

      <main className={styles.main}>
        <div className={styles.contentCard}>
          <header className={styles.header}>
            <h1 className={styles.title}>Select Payment Method</h1>
            <p className={styles.subtitle}>
              Total Amount: <span className={styles.amount}>R {displayAmount}</span>
            </p>
          </header>

          <section
            className={`${styles.methodsGrid} ${
              methods.length === 4 ? styles.methodsGridFour : ""
            } ${
              methods.length === 5 && fromDebtors ? styles.methodsGridFive : ""
            }`}
          >
            {methods.map((method) => {
              const Icon = method.icon;
              const isSelected = selected === method.id;
              return (
                <button
                  key={method.id}
                  type="button"
                  className={`${styles.methodCard} ${method.className} ${
                    isSelected ? styles.methodCardSelected : ""
                  } ${method.id === "split" ? styles.splitCentered : ""}`}
                  disabled={method.id === "credit" && !hasCustomer}
                  onClick={() => {
                    setSelected(method.id);
                    if (method.id === "credit" && !hasCustomer) return;
                    if (method.id === "cash") {
                      navigate(`${basePath}/cash`, {
                        state: { ...location.state, total: amount, fromDebtors },
                      });
                    }
                    if (method.id === "credit") {
                      navigate("/frontshop/payment-types/credit/balance", {
                        state: { total: amount, fromDebtors },
                      });
                    }
                    if (method.id === "card") {
                      navigate(`${basePath}/card`, {
                        state: {
                          ...location.state,
                          total: amount,
                          paymentMethod: "Card",
                          fromDebtors,
                        },
                      });
                    }
                    if (method.id === "nedbank") {
                      navigate(`${basePath}/card`, {
                        state: {
                          ...location.state,
                          total: amount,
                          paymentMethod: "Nedbank",
                          fromDebtors,
                        },
                      });
                    }
                    if (method.id === "ikhokha") {
                      navigate(`${basePath}/card`, {
                        state: {
                          ...location.state,
                          total: amount,
                          paymentMethod: "Ikhokha",
                          fromDebtors,
                        },
                      });
                    }
                    if (method.id === "eft") {
                      navigate(`${basePath}/card`, {
                        state: {
                          ...location.state,
                          total: amount,
                          paymentMethod: "EFT",
                          fromDebtors,
                        },
                      });
                    }
                    if (method.id === "split") {
                      if (amount == null) return;
                      navigate("/frontshop/payment-types/split", {
                        state: { total: amount, fromDebtors },
                      });
                    }
                  }}
                >
                  <div className={styles.iconWrap}>
                    <Icon />
                  </div>
                  <div className={styles.methodLabel}>{method.label}</div>
                </button>
              );
            })}
          </section>
        </div>
      </main>

      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  );
}
